import type { NotificationItem, NotificationList } from "@herskin/contracts";
import { IconBell as Bell } from "@tabler/icons-react";
import { IconButton } from "@herskin/ui";
import { useEffect, useRef } from "react";
import { formatBeijingTime } from "./presentation";

export type BrowserNotificationState = NotificationPermission | "unsupported";

interface NotificationCenterProps {
  open: boolean;
  list: NotificationList | null;
  browserState: BrowserNotificationState;
  onToggle: () => void;
  onOpen: (item: NotificationItem) => void;
  onEnableBrowser: () => void;
}

function kindLabel(item: NotificationItem) {
  if (item.kind === "run_completed" || item.kind === "cron_completed") return "已完成";
  if (item.kind === "run_failed" || item.kind === "cron_failed") return "失败";
  return "等待处理";
}

/** 顶栏通知中心：只展示服务端受控摘要，永远不复制聊天正文或原始错误。 */
export function NotificationCenter({
  open,
  list,
  browserState,
  onToggle,
  onOpen,
  onEnableBrowser,
}: NotificationCenterProps) {
  const unread = list?.unreadCount ?? 0;
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const closeWhenOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) onToggle();
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onToggle();
    };

    // pointerdown 比 click 更早收起浮层，也不会吞掉老板随后点击的目标控件。
    document.addEventListener("pointerdown", closeWhenOutside);
    document.addEventListener("keydown", closeWithEscape);
    return () => {
      document.removeEventListener("pointerdown", closeWhenOutside);
      document.removeEventListener("keydown", closeWithEscape);
    };
  }, [onToggle, open]);

  return (
    <div className="notification-center" ref={root}>
      <IconButton
        aria-label={unread ? `打开通知，${unread} 条未读` : "打开通知"}
        aria-expanded={open}
        onClick={onToggle}
      >
        <Bell />
        {unread ? <span className="notification-count">{Math.min(unread, 99)}</span> : null}
      </IconButton>
      {open ? (
        <section className="notification-popover" aria-label="通知中心">
          <header>
            <div>
              <strong>通知</strong>
              <span>{unread ? `${unread} 条未读` : "没有未读通知"}</span>
            </div>
          </header>
          <div className="notification-browser-setting">
            {browserState === "granted" ? (
              <span>浏览器提醒已开启</span>
            ) : browserState === "denied" ? (
              <span>浏览器已阻止提醒，可在站点权限中恢复</span>
            ) : browserState === "unsupported" ? (
              <span>当前浏览器不支持系统提醒</span>
            ) : (
              <button type="button" onClick={onEnableBrowser}>
                开启浏览器提醒
              </button>
            )}
          </div>
          <div className="notification-list">
            {list?.items.length ? (
              list.items.map((item) => (
                <button
                  type="button"
                  className={`notification-item ${item.readAt ? "is-read" : "is-unread"}`}
                  key={item.notificationId}
                  onClick={() => onOpen(item)}
                >
                  <span className={`notification-kind notification-kind--${item.kind}`}>
                    {kindLabel(item)}
                  </span>
                  <strong>{item.title}</strong>
                  <span>{item.detail}</span>
                  <time dateTime={item.createdAt}>{formatBeijingTime(item.createdAt)}</time>
                </button>
              ))
            ) : (
              <div className="notification-empty">任务完成、失败或等待处理时会显示在这里。</div>
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}
